import { BaseURL } from "../../../service/Api";
import {
  CREW_DOCUMENT_CATEGORIES,
  CrewDocumentCategory,
  CrewDocumentRecord,
  UNKNOWN_CATEGORY_DESCRIPTION,
  getDocumentExtension,
  getDocumentName,
} from "../types/crew-documents";

export interface CrewDocumentSection extends CrewDocumentCategory {
  documents: CrewDocumentRecord[];
}

/**
 * Groups documents into the standard categories, then appends a section for
 * every unrecognised `type_name` found in the data — categories are free text,
 * so records created elsewhere must never be dropped.
 */
export const groupDocumentsByCategory = (
  documents: CrewDocumentRecord[],
): CrewDocumentSection[] => {
  const knownTypes = new Set(
    CREW_DOCUMENT_CATEGORIES.map((category) => category.type_name),
  );

  const knownSections: CrewDocumentSection[] = CREW_DOCUMENT_CATEGORIES.map(
    (category) => ({
      ...category,
      documents: documents.filter(
        (record) => record.type_name === category.type_name,
      ),
    }),
  );

  const unknownSections: CrewDocumentSection[] = [];
  documents.forEach((record) => {
    const typeName = record.type_name || "Uncategorised";
    if (knownTypes.has(typeName)) return;

    const existing = unknownSections.find(
      (section) => section.type_name === typeName,
    );
    if (existing) {
      existing.documents.push(record);
      return;
    }
    unknownSections.push({
      type_name: typeName,
      description: UNKNOWN_CATEGORY_DESCRIPTION,
      required: false,
      documents: [record],
    });
  });

  return [...knownSections, ...unknownSections];
};

export const countRequiredUploaded = (documents: CrewDocumentRecord[]) => {
  const required = CREW_DOCUMENT_CATEGORIES.filter(
    (category) => category.required,
  );
  const uploaded = required.filter((category) =>
    documents.some((record) => record.type_name === category.type_name),
  );
  return { uploaded: uploaded.length, total: required.length };
};

const apiOrigin = (() => {
  try {
    return new URL(BaseURL).origin;
  } catch {
    return "";
  }
})();

/**
 * Rewrites an absolute media URL to a same-origin path during development.
 *
 * In production the build is served from the API host, so media is already
 * same-origin. On localhost it is not, and the API host serves `/media/` with
 * no `Access-Control-Allow-Origin` (WhiteNoise runs ahead of CorsMiddleware)
 * and `X-Frame-Options: sameorigin` — pdf.js can neither read nor frame the
 * file. Dropping the origin sends the request through the dev server's `/media`
 * proxy in `src/setupProxy.js`, restoring the production behaviour.
 */
export const toSameOriginUrl = (uri: string): string => {
  if (process.env.NODE_ENV !== "development") return uri;
  if (!uri || !apiOrigin || !uri.startsWith(apiOrigin)) return uri;

  const path = uri.slice(apiOrigin.length);
  // Only `/media` is proxied; anything else has to stay absolute.
  return path.startsWith("/media/") ? path : uri;
};

/** What the in-app viewer can render natively. */
export type DocumentPreviewKind = "pdf" | "image" | "unsupported";

const IMAGE_EXTENSIONS = ["PNG", "JPG", "JPEG", "GIF", "WEBP", "BMP", "SVG"];

export const getPreviewKind = (uri: string): DocumentPreviewKind => {
  const extension = getDocumentExtension(uri);
  if (extension === "PDF") return "pdf";
  if (IMAGE_EXTENSIONS.includes(extension)) return "image";
  return "unsupported";
};

export const openDocument = (uri: string) => {
  if (typeof window === "undefined" || !uri) return;
  window.open(toSameOriginUrl(uri), "_blank", "noopener,noreferrer");
};

/**
 * The `download` attribute is only honoured same-origin; cross-origin the
 * browser falls back to opening the file in a new tab.
 */
export const downloadDocument = (uri: string) => {
  if (typeof window === "undefined" || !uri) return;

  const link = window.document.createElement("a");
  link.href = toSameOriginUrl(uri);
  link.download = getDocumentName(uri);
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
};
